/**
 * Redux Analytics Middleware
 * Automatically tracks Redux actions for analytics
 */

import type { Middleware, AnyAction } from '@spcc/core'
import { EXCLUDED_ACTIONS, REDUX_ACTION_PATTERNS } from '../../domain/constants'
import { analytics } from '../service'

/**
 * Options for analytics middleware
 */
export interface AnalyticsMiddlewareOptions {
  /** Actions to exclude from tracking */
  excludeActions?: string[]
  /** Only track actions matching these patterns */
  includePatterns?: RegExp[]
  /** Track action payloads (be careful with PII) */
  trackPayloads?: boolean
  /** Enable debug logging */
  debug?: boolean
}

/**
 * Extract category from action type
 * e.g., "orders/createOrderSuccess" -> "orders"
 */
function extractCategory(actionType: string): string {
  const parts = actionType.split('/')
  return parts[0] || 'unknown'
}

/**
 * Extract action name from action type
 * e.g., "orders/createOrderSuccess" -> "createOrderSuccess"
 */
function extractActionName(actionType: string): string {
  const parts = actionType.split('/')
  return parts.slice(1).join('/') || actionType
}

/**
 * Check if action should be tracked
 */
function shouldTrackAction(
  actionType: string,
  excludeActions: string[],
  includePatterns?: RegExp[]
): boolean {
  // Check exclusions first
  const allExcluded = [...EXCLUDED_ACTIONS, ...excludeActions]
  if (allExcluded.some((excluded) => actionType.includes(excluded))) {
    return false
  }

  // If include patterns specified, check them
  if (includePatterns && includePatterns.length > 0) {
    return includePatterns.some((pattern) => pattern.test(actionType))
  }

  // By default, track success/failure actions
  return (
    REDUX_ACTION_PATTERNS.SUCCESS.test(actionType) ||
    REDUX_ACTION_PATTERNS.FAILURE.test(actionType)
  )
}

/**
 * Create analytics middleware for Redux store
 */
export function createAnalyticsMiddleware(
  options: AnalyticsMiddlewareOptions = {}
): Middleware {
  const {
    excludeActions = [],
    includePatterns,
    trackPayloads = false,
    debug = false,
  } = options

  return (_store) => (next) => (action: any) => {
    // Execute action first
    const result = next(action)

    // Skip if no action type
    if (!action.type) return result

    // Check if we should track this action
    if (!shouldTrackAction(action.type, excludeActions, includePatterns)) {
      return result
    }

    const category = extractCategory(action.type)
    const actionName = extractActionName(action.type)

    // Build properties
    const properties: Record<string, unknown> = {
      actionType: action.type,
    }

    // Add payload info if enabled (be careful with PII)
    if (trackPayloads && action.payload !== undefined) {
      properties.hasPayload = true
      properties.payloadType = typeof action.payload
    }

    // Determine if this is a success or failure
    const isSuccess = REDUX_ACTION_PATTERNS.SUCCESS.test(action.type)
    const isFailure = REDUX_ACTION_PATTERNS.FAILURE.test(action.type)

    if (isFailure && action.payload) {
      properties.errorMessage =
        typeof action.payload === 'string'
          ? action.payload
          : action.payload.message || 'Unknown error'
    }

    // Track the action
    analytics.trackEvent({
      eventName: 'redux_action',
      category: category as never,
      action: actionName,
      properties,
    })

    if (debug) {
      console.log('[Analytics Middleware] Tracked:', action.type, properties)
    }

    return result
  }
}

/**
 * Default middleware configuration
 */
export const defaultAnalyticsMiddleware = createAnalyticsMiddleware({
  excludeActions: ['sidebar/toggle', 'theme/setTheme', 'drawer/'],
  includePatterns: [REDUX_ACTION_PATTERNS.SUCCESS, REDUX_ACTION_PATTERNS.FAILURE],
  trackPayloads: false,
  debug: false,
})
